import { getDealDetailsUrl, getLeadDetailsUrl } from "@/routes/paths";
import { globalSearch, TSearchDealItem, TSearchLeadItem, TSearchRecordingItem } from "@/services/search.service";
import { avatarColor } from "@/utils/call-center.utils";
import { fCurrency } from "@/utils/formatNumber";
import { getCloudRecordingDetailsUrl } from "@/utils/paths";
import { getInitials } from "@/utils/strings";
import type { LucideIcon } from "lucide-react";
import { ArrowUpRight, CornerDownLeft, DollarSign, Loader2, Mic, Search, Video, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

/**
 * Global search palette (demo-ui GlobalSearch) over the backend's GET /search
 * grouped endpoint: Pages (client-side navigation), Leads, Deals and
 * Recordings. Navigation uses each item's homogeneous `ref` (lead id /
 * deal id / recording sessionId as string).
 */

export type SearchPage = {
  path: string;
  title: string;
  icon?: LucideIcon;
};

type SearchItem =
  | { kind: "page"; page: SearchPage }
  | { kind: "lead"; lead: TSearchLeadItem }
  | { kind: "deal"; deal: TSearchDealItem }
  | { kind: "recording"; recording: TSearchRecordingItem };

const GROUP_LIMIT = 6;

const formatDuration = (seconds?: number) => {
  if (seconds == null || isNaN(seconds)) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

type Props = {
  open: boolean;
  onClose: () => void;
  pages: SearchPage[];
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-1 mt-2 px-4 text-[9.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 first:mt-0">
    {children}
  </div>
);

const KindBadge = ({ label }: { label: string }) => (
  <span className="hidden rounded-md bg-white/[0.04] px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-slate-400 sm:inline-block">
    {label}
  </span>
);

const GlobalSearch = ({ open, onClose, pages }: Props) => {
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [leads, setLeads] = useState<TSearchLeadItem[]>([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [deals, setDeals] = useState<TSearchDealItem[]>([]);
  const [dealsTotal, setDealsTotal] = useState(0);
  const [recordings, setRecordings] = useState<TSearchRecordingItem[]>([]);
  const [recordingsTotal, setRecordingsTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const trimmed = query.trim();
  const hasQuery = trimmed.length > 0;

  const resetResults = () => {
    setLeads([]);
    setLeadsTotal(0);
    setDeals([]);
    setDealsTotal(0);
    setRecordings([]);
    setRecordingsTotal(0);
  };

  // Reset on every open
  useEffect(() => {
    if (open) {
      setQuery("");
      resetResults();
      setActiveIdx(0);
    }
  }, [open]);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Debounced grouped search (GET /search)
  useEffect(() => {
    if (!open) return;
    if (trimmed.length < 2) {
      resetResults();
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await globalSearch(trimmed, GROUP_LIMIT);
        setLeads(response.leads?.items?.filter((i) => i.type === "lead") ?? []);
        setLeadsTotal(response.leads?.total ?? 0);
        setDeals(response.deals?.items?.filter((i) => i.type === "deal") ?? []);
        setDealsTotal(response.deals?.total ?? 0);
        setRecordings(response.recordings?.items?.filter((i) => i.type === "recording") ?? []);
        setRecordingsTotal(response.recordings?.total ?? 0);
      } catch {
        resetResults();
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [trimmed, open]);

  const pageMatches = useMemo(
    () => (hasQuery ? pages.filter((p) => p.title.toLowerCase().includes(trimmed.toLowerCase())) : pages),
    [hasQuery, pages, trimmed]
  );
  const items: SearchItem[] = useMemo(
    () => [
      ...pageMatches.map((page): SearchItem => ({ kind: "page", page })),
      ...(hasQuery ? leads.map((lead): SearchItem => ({ kind: "lead", lead })) : []),
      ...(hasQuery ? deals.map((deal): SearchItem => ({ kind: "deal", deal })) : []),
      ...(hasQuery ? recordings.map((recording): SearchItem => ({ kind: "recording", recording })) : []),
    ],
    [pageMatches, hasQuery, leads, deals, recordings]
  );

  useEffect(() => {
    setActiveIdx(0);
  }, [trimmed, leads.length, deals.length, recordings.length]);

  const selectItem = useCallback(
    (item: SearchItem) => {
      onClose();
      if (item.kind === "page") navigate(item.page.path);
      else if (item.kind === "lead") navigate(getLeadDetailsUrl(item.lead.ref));
      else if (item.kind === "deal") navigate(getDealDetailsUrl(item.deal.ref));
      else navigate(getCloudRecordingDetailsUrl(item.recording.ref));
    },
    [navigate, onClose]
  );

  // Keyboard: esc close, arrows move, enter selects
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (items.length ? (i + 1) % items.length : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => (items.length ? (i - 1 + items.length) % items.length : 0));
      } else if (e.key === "Enter") {
        const item = items[activeIdx];
        if (item) {
          e.preventDefault();
          selectItem(item);
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, items, activeIdx, onClose, selectItem]);

  // Keep the active row in view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  if (!open) return null;

  const noResults = hasQuery && !loading && items.length === 0;

  return createPortal(
    <div className="fixed inset-0 z-[140] flex items-start justify-center overflow-y-auto px-4 pt-[10vh]">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
      <div className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-b from-ink-800/95 to-ink-900/95 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.7)] backdrop-blur-xl animate-fade-in-up-fast">
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-accent-purple/15 blur-3xl" />

        {/* Input */}
        <div className="relative flex items-center gap-3 border-b border-white/[0.06] px-5 py-4">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search leads, deals, recordings or jump to a page..."
            className="h-full flex-1 bg-transparent text-[15px] text-white placeholder:text-slate-500 focus:outline-none"
          />
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" />}
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear"
              className="grid h-6 w-6 place-items-center rounded-md text-slate-500 transition hover:bg-white/[0.04] hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <kbd className="hidden items-center gap-1 rounded-md border border-white/[0.08] bg-white/[0.04] px-1.5 py-0.5 text-[10px] font-medium text-slate-400 md:inline-flex">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="relative max-h-[55vh] overflow-y-auto py-2">
          {noResults ? (
            <div className="grid place-items-center px-6 py-12 text-center">
              <div className="grid h-12 w-12 place-items-center rounded-xl border border-white/[0.06] bg-white/[0.02] text-slate-400">
                <Search className="h-5 w-5" />
              </div>
              <div className="mt-3 text-[14px] font-semibold text-white">No results for “{trimmed}”</div>
              <p className="mt-1 max-w-xs text-[11.5px] text-slate-500">Try a lead name, deal name, email or phone number.</p>
            </div>
          ) : (
            <>
              {pageMatches.length > 0 && <SectionLabel>{hasQuery ? "Pages" : "Jump to"}</SectionLabel>}
              {items.map((item, idx) => {
                const active = idx === activeIdx;
                const rowClass = cn(
                  "relative flex w-full items-center gap-3 px-4 py-2.5 text-left transition",
                  active ? "bg-gradient-to-r from-brand-500/10 to-transparent" : "hover:bg-white/[0.02]"
                );
                const accentBar = active && (
                  <span className="absolute inset-y-1.5 left-0 w-[3px] rounded-r-full bg-gradient-to-b from-brand-300 to-brand-500 shadow-[0_0_12px_rgba(59,130,246,0.7)]" />
                );
                const arrow = (
                  <ArrowUpRight
                    className={cn("h-3.5 w-3.5 shrink-0 transition", active ? "translate-x-0.5 text-brand-200" : "text-slate-600")}
                  />
                );

                if (item.kind === "page") {
                  const Icon = item.page.icon;
                  return (
                    <button key={`page-${item.page.path}`} type="button" data-idx={idx} onClick={() => selectItem(item)} onMouseEnter={() => setActiveIdx(idx)} className={rowClass}>
                      {accentBar}
                      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/[0.04] text-slate-300">
                        {Icon ? <Icon className="h-4 w-4" /> : <Search className="h-4 w-4" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[12.5px] font-semibold text-white">{item.page.title}</div>
                        <div className="truncate text-[10.5px] text-slate-500">{item.page.path}</div>
                      </div>
                      <KindBadge label="Page" />
                      {arrow}
                    </button>
                  );
                }

                if (item.kind === "lead") {
                  const isFirstLeadRow = items.findIndex((i) => i.kind === "lead") === idx;
                  return (
                    <div key={`lead-${item.lead.id}`}>
                      {isFirstLeadRow && (
                        <SectionLabel>
                          Leads ({leads.length}
                          {leadsTotal > leads.length ? ` of ${leadsTotal}` : ""})
                        </SectionLabel>
                      )}
                      <button type="button" data-idx={idx} onClick={() => selectItem(item)} onMouseEnter={() => setActiveIdx(idx)} className={rowClass}>
                        {accentBar}
                        <div
                          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[10px] font-semibold text-white ring-1 ring-white/10"
                          style={{ background: avatarColor(item.lead.title || "?") }}
                        >
                          {getInitials(item.lead.title || "?")}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-[12.5px] font-semibold text-white">{item.lead.title}</span>
                            {item.lead.status && (
                              <span
                                className="shrink-0 rounded-md px-1.5 py-0.5 text-[9.5px] font-semibold"
                                style={{ color: item.lead.status.color, background: `${item.lead.status.color}1A` }}
                              >
                                {item.lead.status.label}
                              </span>
                            )}
                          </div>
                          <div className="truncate text-[10.5px] text-slate-500">
                            {item.lead.subtitle || item.lead.meta?.email}
                          </div>
                        </div>
                        <KindBadge label="Lead" />
                        {arrow}
                      </button>
                    </div>
                  );
                }

                if (item.kind === "recording") {
                  const isFirstRecordingRow = items.findIndex((i) => i.kind === "recording") === idx;
                  const isVideo = item.recording.meta?.channel?.toLowerCase() === "video";
                  const recSub = [
                    formatDuration(item.recording.meta?.durationSeconds),
                    item.recording.meta?.agentName,
                    item.recording.meta?.leadName,
                  ].filter(Boolean);
                  return (
                    <div key={`recording-${item.recording.ref}`}>
                      {isFirstRecordingRow && (
                        <SectionLabel>
                          Recordings ({recordings.length}
                          {recordingsTotal > recordings.length ? ` of ${recordingsTotal}` : ""})
                        </SectionLabel>
                      )}
                      <button type="button" data-idx={idx} onClick={() => selectItem(item)} onMouseEnter={() => setActiveIdx(idx)} className={rowClass}>
                        {accentBar}
                        <div
                          className={cn(
                            "grid h-8 w-8 shrink-0 place-items-center rounded-lg ring-1",
                            isVideo
                              ? "bg-accent-purple/10 text-accent-purple ring-accent-purple/20"
                              : "bg-accent-green/10 text-accent-green ring-accent-green/20"
                          )}
                        >
                          {isVideo ? <Video className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-[12.5px] font-semibold text-white">{item.recording.title}</span>
                            {item.recording.status && (
                              <span
                                className="shrink-0 rounded-md px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider"
                                style={{ color: item.recording.status.color, background: `${item.recording.status.color}1A` }}
                              >
                                {item.recording.status.label}
                              </span>
                            )}
                          </div>
                          <div className="truncate text-[10.5px] text-slate-500">
                            {recSub.length > 0
                              ? recSub.map((part, i) => (
                                  <span key={i}>
                                    {i > 0 && <span className="text-slate-700"> · </span>}
                                    {part}
                                  </span>
                                ))
                              : item.recording.subtitle}
                          </div>
                        </div>
                        <KindBadge label="Recording" />
                        {arrow}
                      </button>
                    </div>
                  );
                }

                const isFirstDealRow = items.findIndex((i) => i.kind === "deal") === idx;
                const dealMeta = item.deal.meta;
                const dealSub = [
                  dealMeta?.amount != null ? `${fCurrency(dealMeta.amount)} ${dealMeta.currency ?? ""}`.trim() : null,
                  dealMeta?.pipelineName,
                  dealMeta?.ownerName,
                ].filter(Boolean);
                return (
                  <div key={`deal-${item.deal.id}`}>
                    {isFirstDealRow && (
                      <SectionLabel>
                        Deals ({deals.length}
                        {dealsTotal > deals.length ? ` of ${dealsTotal}` : ""})
                      </SectionLabel>
                    )}
                    <button type="button" data-idx={idx} onClick={() => selectItem(item)} onMouseEnter={() => setActiveIdx(idx)} className={rowClass}>
                      {accentBar}
                      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent-green/10 text-accent-green ring-1 ring-accent-green/20">
                        <DollarSign className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-[12.5px] font-semibold text-white">{item.deal.title}</span>
                          {item.deal.status && (
                            <span
                              className="shrink-0 rounded-md px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider"
                              style={{ color: item.deal.status.color, background: `${item.deal.status.color}1A` }}
                            >
                              {item.deal.status.label}
                            </span>
                          )}
                        </div>
                        <div className="truncate text-[10.5px] text-slate-500">
                          {dealSub.length > 0 ? (
                            dealSub.map((part, i) => (
                              <span key={i}>
                                {i > 0 && <span className="text-slate-700"> · </span>}
                                {i === 0 && dealMeta?.amount != null ? (
                                  <span className="tabular-nums text-slate-300">{part}</span>
                                ) : (
                                  part
                                )}
                              </span>
                            ))
                          ) : (
                            item.deal.subtitle
                          )}
                        </div>
                      </div>
                      <KindBadge label="Deal" />
                      {arrow}
                    </button>
                  </div>
                );
              })}
              {hasQuery && loading && leads.length === 0 && deals.length === 0 && recordings.length === 0 && (
                <div className="flex items-center gap-2 px-4 py-3 text-[11.5px] text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Searching...
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="relative flex items-center justify-between border-t border-white/[0.06] bg-white/[0.015] px-5 py-2.5 text-[10.5px] text-slate-500">
          <span className="inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5">
              <kbd className="inline-flex h-4 items-center justify-center rounded bg-white/[0.06] px-1 text-[9px]">↑</kbd>
              <kbd className="inline-flex h-4 items-center justify-center rounded bg-white/[0.06] px-1 text-[9px]">↓</kbd>
              to navigate
            </span>
            <span className="inline-flex items-center gap-1.5">
              <kbd className="inline-flex h-4 w-4 items-center justify-center rounded bg-white/[0.06] text-[9px]">
                <CornerDownLeft className="h-2 w-2" />
              </kbd>
              to select
            </span>
          </span>
          <span className="hidden items-center gap-1.5 sm:inline-flex">
            <Search className="h-3 w-3 text-brand-300" />
            Searches across your entire workspace
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GlobalSearch;
